import React, { FC, useState, useEffect } from "react";
import { Box, Modal as MuiModal, TextField, Typography, MenuItem, FormHelperText } from "@mui/material";
import MDBox from "mdComponents/MDBox";
import MDButton from "mdComponents/MDButton";
import MDInput from "mdComponents/MDInput";
import { CorporateAccount } from "../models/corporateAccountModel";
import { states } from "../constants";

interface EditCorporateAccountModalProps {
  visible: boolean;
  account: CorporateAccount;
  onSave: (updated: CorporateAccount) => void;
  onCancel: () => void;
}

const EditCorporateAccountModal: FC<EditCorporateAccountModalProps> = ({
  visible,
  account,
  onSave,
  onCancel,
}) => {
  const [form, setForm] = useState<CorporateAccount>(account);
  const [errors, setErrors] = useState<{ state?: string }>({});

  useEffect(() => {
    if (visible) setForm(account);
  }, [visible, account]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (!form.state) {
      setErrors({ state: "Please select a state" });
      return;
    }
    onSave(form);
  };

  return (
    <MuiModal open={visible} onClose={() => {}}>
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          width: 500,
          maxWidth: "90%",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Typography variant="h5" fontWeight="medium" mb={2}>
          Edit Corporate Account
        </Typography>

        <MDBox display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Business Name"
            name="businessName"
            value={form.businessName}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="First Name"
            name="contactFirstName"
            value={form.contactFirstName}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Last Name"
            name="contactLastName"
            value={form.contactLastName}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Phone"
            name="contactPhone"
            value={form.contactPhone}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Email"
            name="contactEmail"
            value={form.contactEmail}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Address"
            name="address"
            value={form.address}
            onChange={handleChange}
            fullWidth
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="City"
              name="city"
              value={form.city}
              onChange={handleChange}
              fullWidth
            />

            {/* State Dropdown */}
            <MDBox mt={0}>
              <MDInput
                select
                fullWidth
                name="state"
                value={form.state || ""}
                onChange={handleChange}
                displayEmpty
                error={Boolean(errors.state)}
                InputProps={{
                  sx: {
                    height: 45,
                    display: "flex",
                    alignItems: "center",
                    color: (theme) => theme.palette.text.primary,
                    "& .MuiSelect-select": {
                      color: (theme) => theme.palette.text.primary,
                      display: "flex",
                      alignItems: "center",
                      minHeight: "45px",
                    },
                  },
                }}
              >
                <MenuItem value="">State</MenuItem>
                {states.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.name}
                  </MenuItem>
                ))}
              </MDInput>
              {errors.state && (
                <FormHelperText sx={{ ml: 1 }}>{errors.state}</FormHelperText>
              )}
            </MDBox>

            <TextField
              label="ZIP"
              name="zip"
              value={form.zip}
              onChange={handleChange}
              fullWidth
            />
          </Box>

          <TextField
            label="Notes"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
          />
        </MDBox>

        {/* Action Buttons */}
        <MDBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <MDButton variant="outlined" color="secondary" onClick={onCancel}>
            Cancel
          </MDButton>
          <MDButton color="info" onClick={handleSave}>
            Save
          </MDButton>
        </MDBox>
      </Box>
    </MuiModal>
  );
};

export default EditCorporateAccountModal;
